import { ActionMenu } from "@gladis/ui";
import { useT } from "@gladis/ui/i18n";
import { useState, type ReactNode } from "react";

import { useEditStock, useRecordPrice, useRecordTransaction, useRemoveStock } from "../hooks.js";
import { ConfirmDialog } from "./ConfirmDialog.js";
import { PriceForm } from "./PriceForm.js";
import { StockForm } from "./StockForm.js";
import { TransactionForm } from "./TransactionForm.js";

/**
 * What can be done to a stock, and the dialogs that do it.
 *
 * ==========================================================================================
 * ONE IMPLEMENTATION, TWO PLACES
 * ==========================================================================================
 *
 * The list and the detail view offer the same four actions, and the rules around them are not
 * trivial: Remove is offered only while the stock has never been traded, editing a symbol re-prices,
 * and every dialog has to be mounted only while open or it carries the previous entry's values. A
 * second copy on the list would be four rules maintained twice, and the copy that drifts is always
 * the one nobody is looking at.
 *
 * ==========================================================================================
 * `canBeRemoved` IS PASSED IN, NEVER DERIVED
 * ==========================================================================================
 *
 * It counts SOFT-DELETED transactions, which no client-side view of a stock contains. Deriving it
 * from "does this position have transactions" gives the opposite answer for a stock whose every
 * transaction was deleted, and offers a Remove that always fails.
 */

export interface StockActionsProps {
  stockId: string;
  name: string;
  symbol: string;
  exchange: string | null;
  /** Server-computed. See the note above. */
  canBeRemoved: boolean;
  /** Where to go after a removal — the detail view navigates away, the list stays put. */
  onRemoved?: () => void;
  /** Names the menu for a screen reader: "Actions for ASML.AS", not just "Actions". */
  label?: string;
}

export function StockActions({
  stockId,
  name,
  symbol,
  exchange,
  canBeRemoved,
  onRemoved,
  label,
}: StockActionsProps): ReactNode {
  const t = useT();
  const recordTransaction = useRecordTransaction();
  const recordPrice = useRecordPrice();
  const editStock = useEditStock();
  const removeStock = useRemoveStock();

  const [transacting, setTransacting] = useState(false);
  const [pricing, setPricing] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [removing, setRemoving] = useState(false);

  return (
    <>
      <ActionMenu
        label={label ?? `Actions for ${symbol}`}
        items={[
          {
            label: t("inv.actions.recordTransaction"),
            onSelect: () => {
              setTransacting(true);
            },
          },
          {
            label: t("inv.actions.updatePrice"),
            onSelect: () => {
              setPricing(true);
            },
          },
          {
            label: t("inv.actions.editStock"),
            onSelect: () => {
              setRenaming(true);
            },
          },
          // Offered only while the stock has never been traded. Once it has, its transactions are
          // the record, and a control that always errors is worse than no control.
          ...(canBeRemoved
            ? [
                {
                  label: t("inv.actions.removeStock"),
                  destructive: true,
                  onSelect: () => {
                    setRemoving(true);
                  },
                },
              ]
            : []),
        ]}
      />

      {transacting && (
        <TransactionForm
          open
          onClose={() => {
            setTransacting(false);
            recordTransaction.reset();
          }}
          editing={null}
          pending={recordTransaction.isPending}
          error={recordTransaction.error?.message ?? null}
          onSubmit={(input) => {
            recordTransaction.mutate(
              { stockId, ...input },
              {
                onSuccess: () => {
                  setTransacting(false);
                },
              },
            );
          }}
        />
      )}

      {pricing && (
        <PriceForm
          open
          onClose={() => {
            setPricing(false);
            recordPrice.reset();
          }}
          symbol={symbol}
          pending={recordPrice.isPending}
          error={recordPrice.error?.message ?? null}
          onSubmit={(input) => {
            recordPrice.mutate(
              { stockId, ...input },
              {
                onSuccess: () => {
                  setPricing(false);
                },
              },
            );
          }}
        />
      )}

      {renaming && (
        <StockForm
          open
          onClose={() => {
            setRenaming(false);
            editStock.reset();
          }}
          editing={{ name, symbol, exchange }}
          pending={editStock.isPending}
          error={editStock.error?.message ?? null}
          onSubmit={(input) => {
            editStock.mutate(
              { stockId, name: input.name, symbol: input.symbol },
              {
                onSuccess: () => {
                  setRenaming(false);
                },
              },
            );
          }}
        />
      )}

      {removing && (
        <ConfirmDialog
          open
          onClose={() => {
            setRemoving(false);
            removeStock.reset();
          }}
          title={`Remove ${symbol}?`}
          body={t("inv.actions.removeHint")}
          confirmLabel={t("inv.actions.removeStock")}
          pending={removeStock.isPending}
          error={removeStock.error?.message ?? null}
          onConfirm={() => {
            removeStock.mutate(
              { stockId },
              {
                onSuccess: () => {
                  // The list simply loses the row when the query refetches; the detail view has to
                  // navigate, because the record its URL names no longer exists.
                  setRemoving(false);
                  onRemoved?.();
                },
              },
            );
          }}
        />
      )}
    </>
  );
}
